import React, { useState, useEffect, useRef } from "react";
import { auth, db, ref, onValue, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FiCalendar, FiFileText } from "react-icons/fi";
import { DashboardLoading, DashboardNotice, EmptyState, NoCourse } from "./DashboardStates";

const FIREWORK_COLORS = ["#fbbf24", "#34d399", "#60a5fa", "#f472b6", "#fde68a"];

function isAbsent(exam) {
  return !exam.grade || exam.grade === "N/A" || exam.score === "Not Attended";
}

function reactionFor(exam) {
  if (isAbsent(exam)) {
    return { kind: "absent", headline: "Missed", line: "The chair stayed empty." };
  }
  switch (exam.grade) {
    case "A*":
      return { kind: "fireworks", headline: "A*", line: "The mark scheme ran out of things to deduct.", tone: "text-emerald-400" };
    case "A":
      return { kind: "laurel", headline: "A", line: "One breath away from the top.", tone: "text-green-400" };
    case "B":
      return { kind: "nod", headline: "B", line: "A small bow. The good kind.", tone: "text-blue-400" };
    case "C":
      return { kind: "scales", headline: "C", line: "Safely across. The high marks are still on the table.", tone: "text-yellow-400" };
    case "D":
      return { kind: "sprout", headline: "D", line: "A foothold. The wall is still there.", tone: "text-orange-400" };
    default:
      return { kind: "again", headline: exam.grade, line: "This exam doesn't get the last word.", tone: "text-red-400" };
  }
}

function realComment(exam) {
  const comment = (exam.comment || "").trim();
  if (!comment || comment.toLowerCase() === "not attended") return "";
  return comment;
}

function ResultBurst({ active }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;

    const ctx = canvas.getContext("2d");
    const w = (canvas.width = parent.clientWidth);
    const h = (canvas.height = parent.clientHeight);
    const particles = Array.from({ length: 42 }, () => ({
      x: w / 2,
      y: h * 0.72,
      r: 3 + Math.random() * 4,
      color: FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)],
      vy: -(3 + Math.random() * 5),
      vx: (Math.random() - 0.5) * 7,
      rotation: Math.random() * 360,
      spin: (Math.random() - 0.5) * 12,
    }));
    const start = Date.now();
    let frame;

    const draw = () => {
      const elapsed = (Date.now() - start) / 1000;
      if (elapsed > 2.4) {
        ctx.clearRect(0, 0, w, h);
        return;
      }
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08;
        p.rotation += p.spin;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 1.4);
        ctx.restore();
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [active]);

  if (!active) return null;
  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden />;
}

function FireworkGlow() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-emerald-400" aria-hidden>
      <circle cx="32" cy="34" r="4" fill="currentColor" />
      <circle cx="32" cy="34" r="12" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.75" />
      <circle cx="32" cy="34" r="20" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
      <circle cx="32" cy="10" r="2" fill="currentColor" />
      <circle cx="52" cy="24" r="2" fill="#fbbf24" />
      <circle cx="48" cy="48" r="2" fill="#60a5fa" />
      <circle cx="16" cy="46" r="2" fill="#f472b6" />
      <circle cx="12" cy="22" r="2" fill="#fde68a" />
    </svg>
  );
}

function Laurel() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-green-400" aria-hidden>
      <path d="M32 50c-8-6-14-16-12-28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M32 50c8-6 14-16 12-28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M22 40c-4-1-6-4-5-7 3 1 5 3 5 7zM18 32c-4 0-7-2-7-5 3 0 6 1 7 5zM20 24c-3 1-6-1-7-4 4 0 6 2 7 4z" fill="currentColor" />
      <path d="M42 40c4-1 6-4 5-7-3 1-5 3-5 7zM46 32c4 0 7-2 7-5-3 0-6 1-7 5zM44 24c3 1 6-1 7-4-4 0-6 2-7 4z" fill="currentColor" />
    </svg>
  );
}

function BowingFigure() {
  return (
    <svg viewBox="0 0 64 64" className="polite-nod h-16 w-16 text-blue-400" aria-hidden>
      <circle cx="40" cy="16" r="6" fill="currentColor" />
      <path d="M38 22c8 2 14 8 16 16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M36 28c-8 6-16 8-24 6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M30 40c2 8 2 14 0 18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M30 48c8 2 14 2 20 0" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function EmptyChair() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-gray-400" aria-hidden>
      <path d="M16 18h6v20H16zM42 18h6v20h-6z" fill="currentColor" />
      <path d="M14 36h36v6H14z" fill="currentColor" />
      <path d="M18 42h4v12h-4zM42 42h4v12h-4z" fill="currentColor" />
      <path d="M8 54h48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <rect x="24" y="28" width="14" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function Scales() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-yellow-400" aria-hidden>
      <path d="M32 14v34M20 50h24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M14 24h36" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M14 24l-6 12h12zM50 24l-6 12h12z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function Sprout() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-orange-400" aria-hidden>
      <path d="M32 50V30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M32 34c-8-2-12-10-10-16 8 2 12 8 10 16zM32 38c8-1 14-8 12-14-8 1-12 7-12 14z" fill="currentColor" />
      <path d="M18 52h28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

function FreshPage() {
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 text-red-400" aria-hidden>
      <path d="M20 12h18l10 10v30H20z" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M38 12v10h10" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <path d="M26 34h14M26 42h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

const Exams = () => {
  const [exams, setExams] = useState([]);
  const [username, setUsername] = useState("Loading...");
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [courseMissing, setCourseMissing] = useState(false);
  const [openIds, setOpenIds] = useState({});
  const [burstId, setBurstId] = useState(null);
  const celebrated = useRef(new Set());
  const { enrolledCourse } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            setUsername(foundUser.name);
            setUserId(foundUser.studentId);
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser || !userCourse) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  setUsername(student.name);
                  setUserId(student.studentId);
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (!foundUser || !userCourse) {
          setCourseMissing(true);
          setLoading(false);
          return;
        }

        // Fetch all exams from the course
        const courseRef = ref(db, `courses/${userCourse}`);
        const courseSnapshot = await get(courseRef);

        if (courseSnapshot.exists()) {
          const courseData = courseSnapshot.val();
          const courseExams = courseData.exams || {};

          // Get student's exam grades
          const studentExamGrades = foundUser.examGrades || {};

          // Format all exams, marking unattended ones
          const formattedExams = Object.entries(courseExams)
            .map(([examId, examDetails]) => {
              const studentGrade = studentExamGrades[examId];
              return {
                id: examId,
                name: examDetails.name || examId,
                score: studentGrade?.mark || "Not Attended",
                total: examDetails.totalMark || 100,
                grade: studentGrade?.grade || "N/A",
                comment: studentGrade?.comment || "Not attended",
                published: examDetails.published !== false, // Default to true
                date: examDetails.date || "Not specified"
              };
            })
            .filter(exam => exam.published) // Only show published exams
            .sort((a, b) => {
              // Sort by date if available, otherwise by name
              if (a.date && b.date) {
                return new Date(b.date) - new Date(a.date);
              }
              return a.name.localeCompare(b.name);
            });

          setExams(formattedExams);
        } else {
          setExams([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const openResult = (exam) => {
    const willOpen = !openIds[exam.id];
    setOpenIds((current) => ({ ...current, [exam.id]: willOpen }));
    if (willOpen && reactionFor(exam).kind === "fireworks" && !celebrated.current.has(exam.id)) {
      celebrated.current.add(exam.id);
      setBurstId(exam.id);
      window.setTimeout(() => {
        setBurstId((current) => (current === exam.id ? null : current));
      }, 2600);
    }
  };

  if (loading) return <DashboardLoading />;
  if (courseMissing) return <NoCourse description="Once you're enrolled, your exam grades will show up here." />;
  if (error) return <DashboardNotice message={error} />;

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Exams</h1>
            <p className="text-blue-100">Open a paper to see the result.</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-blue-100 text-sm md:text-base">Total Exams:</span>
            <span className="text-white font-bold text-lg md:text-xl">{exams.length}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {exams.length > 0 ? (
          exams.map((exam) => {
            const open = !!openIds[exam.id];
            const reaction = reactionFor(exam);
            const comment = realComment(exam);
            const showDate = exam.date && exam.date !== "Not specified";
            return (
              <article
                key={exam.id}
                className="dash-card overflow-hidden rounded-2xl border border-gray-700/50 bg-gray-800/50"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
                  onClick={() => openResult(exam)}
                  aria-expanded={open}
                >
                  <span className="flex min-w-0 items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-500/20 text-blue-400">
                      <FiFileText className="text-xl" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                        {open ? "Result" : "Sealed result"}
                      </span>
                      <span className="mt-1 block truncate text-lg font-semibold text-white">{exam.name}</span>
                      {showDate && (
                        <span className="mt-1 flex items-center gap-1 text-sm text-gray-400">
                          <FiCalendar />
                          {exam.date}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-medium text-blue-400">{open ? "Close" : "Open"}</span>
                </button>

                {open && (
                  <div className="result-reveal relative border-t border-gray-700/40 px-6 pb-8 pt-2">
                    <div className="relative flex h-28 items-center justify-center">
                      <ResultBurst active={burstId === exam.id} />
                      {reaction.kind === "fireworks" && <FireworkGlow />}
                      {reaction.kind === "laurel" && <Laurel />}
                      {reaction.kind === "nod" && <BowingFigure />}
                      {reaction.kind === "absent" && <EmptyChair />}
                      {reaction.kind === "scales" && <Scales />}
                      {reaction.kind === "sprout" && <Sprout />}
                      {reaction.kind === "again" && <FreshPage />}
                    </div>
                    <p className={`text-center text-6xl font-black tracking-tight ${reaction.tone || "text-gray-300"}`}>
                      {reaction.headline}
                    </p>
                    <p className="mx-auto mt-2 max-w-sm text-center text-base text-gray-300">{reaction.line}</p>
                    {!isAbsent(exam) && (
                      <p className="mt-3 text-center text-sm text-gray-400">
                        {exam.score}/{exam.total}
                      </p>
                    )}
                    {comment && (
                      <p className="dash-inset mx-auto mt-5 max-w-lg rounded-xl bg-gray-700/30 px-4 py-3 text-center text-sm text-gray-300">
                        {comment}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })
        ) : (
          <EmptyState
            icon={FiFileText}
            title="No exam grades yet"
            description="Scores and comments will appear here after an exam is published."
          />
        )}
      </div>
    </div>
  );
};

export default Exams;